<template>
  <!--
    Single root + inheritAttrs: false so fallthrough attrs (e.g. @playing from VBuffer, :class z-index)
    bind here — not on VFrame, which uses a fragment (main tree + Teleport) and cannot inherit listeners.
  -->
  <!--
    When hidden, hide the whole slot (not only inner opacity-0): Chrome can still composite decoded
    video in a layer that ignores ancestor opacity, so the preload slot could flash a first frame on top.
  -->
  <div
    class="absolute inset-0 min-h-0"
    :class="[
      show && mainFacetIsNativeVideo ? 'bg-transparent' : 'bg-black',
      show ? '' : 'invisible pointer-events-none overflow-hidden [contain:paint]',
      show && !isRssDirectoryQueueFrame && mainFacetRendersImage ? 'isolate z-[30]' : '',
    ]"
    :data-lucimark-native-underlay-shell="show && mainFacetIsNativeVideo ? '1' : undefined"
    v-bind="$attrs"
  >
    <!-- Chrome can composite a paused/hidden video above the other buffer slot; mask the whole hidden frame.
         Keep this mask opaque even when html underlay chrome is punched (do not use underlay-shell). -->
    <div
      v-if="!show"
      class="absolute inset-0 z-[80] bg-black"
      aria-hidden="true"
    />
    <RssDirectoryFrameHost
      v-if="isRssDirectoryQueueFrame"
      :frame="frame"
      :rotation="rotation"
      :visible="show"
    />
    <VFrame
      v-else
      ref="frameRef"
      :frame="frame"
      :rotation="rotation"
    >
      <template
        v-for="section of Object.keys(frame.facets)"
        :key="section"
        #[section]="{ facet }"
      >
        <!-- All widgets: placeholder only; actual iframe is in WidgetTwoSlotLayer (two fixed slots, visibility swap). -->
        <SuppressedSectionNotice
          v-if="isWidgetEntry(resolveAssetEntry(facet)) && widgetSuppressionReason(facet)"
          :ref="(el) => setWidgetSectionRef(section, el, bufferIndex ?? 0)"
          :reason="widgetSuppressionReason(facet)!"
          class="w-full h-full"
          :class="show ? 'opacity-100' : 'opacity-0'"
        />
        <div
          v-else-if="isWidgetEntry(resolveAssetEntry(facet))"
          :ref="(el) => setWidgetSectionRef(section, el, bufferIndex ?? 0)"
          class="w-full h-full bg-black"
          :class="show ? 'opacity-100' : 'opacity-0'"
          aria-hidden="true"
        />
        <template v-else-if="nativeFacetEntryWithVnode(facet)">
          <div
            v-if="shouldMountNativeFacet"
            class="w-full h-full"
            :class="show ? 'opacity-100' : 'opacity-0'"
          >
            <NativeFacetVNodeHost
              :asset-entry="nativeFacetEntryWithVnode(facet)!"
              :facet="facetWithAsset(facet)"
              :buffer-index="bufferIndex ?? 0"
              :show="show"
              :unmuted="facet.unmuted ?? false"
              :preload-next-slot-now="effectivePreloadNextSlotNow"
              :seamless-incoming-from-link="seamlessIncomingFromLink"
              :continuous-media-offset-seconds="continuousMediaOffsetSeconds"
              :presentation-hold-overlay="presentationHoldOverlay"
              :section-fit="frame.layout.sections[section]?.fit"
            />
          </div>
        </template>
        <SuppressedSectionNotice
          v-else
          :reason="fallbackSectionReason(facet)"
          class="w-full h-full"
          :class="show ? 'opacity-100' : 'opacity-0'"
        />
      </template>
      <template #overlay>
        <FrameCtaOverlayLayer
          v-if="frame.ctas?.length"
          :frame="frame"
          :layout="frame.layout"
          :cta-registry="state.ctaRegistry"
          :interpolation-context="ctaInterpolationContext"
          :visible="show"
        />
      </template>
    </VFrame>
  </div>
</template>

<script lang="ts" setup>
import type { IScheduleRootAssignment } from '@lucimark/shared/models/player-registration-payload'
import type { TFacet } from '@lucimark/shared/models/playlist'
import type { Ref, VNode } from 'vue'
import type { TRotation } from '@/components/VFrame.vue'
import type { IAssetEntry, IAssetIndex, IQueueFrame } from '@/types'
import { facetAssetIndexKey } from '@lucimark/shared/helpers/facetAssetIndexKey'
import { isRssDirectoryFrame } from '@lucimark/shared/helpers/rssDirectoryFrame'
import { computed, inject, onBeforeUnmount, onMounted, onUpdated, ref, toRefs, unref, watch } from 'vue'
import FrameCtaOverlayLayer from '@/components/FrameCtaOverlayLayer.vue'
import NativeFacetVNodeHost from '@/components/NativeFacetVNodeHost.vue'
import RssDirectoryFrameHost from '@/components/rss/RssDirectoryFrameHost.vue'

import SuppressedSectionNotice from '@/components/SuppressedSectionNotice.vue'
import VFrame from '@/components/VFrame.vue'
import usePlayer from '@/composables/usePlayer'
import { buildCtaInterpolationContext } from '@/helpers/buildCtaInterpolationContext'
import { continuousMediaOffsetSecondsForQueueFrame } from '@/helpers/continuousMediaPlayback'
import { assetEntryMediaKind } from '@/helpers/playbackAssetHelpers'
import { getSectionFallbackSuppressionReason } from '@/helpers/sectionPlaybackFallback'
import { getActiveWidgetSectionKey, getWidgetSectionSuppressionReason, isWidgetEntry } from '@/helpers/widgetSlotHelpers'

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    frame: IQueueFrame
    /** Index in the frame buffer (0 or 1); used to set per-slot section/container refs for prepositioning. */
    bufferIndex?: number
    rotation?: TRotation
    show: boolean
    assetIndex: IAssetIndex
    /** Incoming frame after a playlist “continuous” link; tightens video show timing. */
    seamlessIncomingFromLink?: boolean
    /**
     * Sticky outgoing overlay (Phase B): keep pixels visible without driving playback / PoP.
     */
    presentationHoldOverlay?: boolean
  }>(),
  { seamlessIncomingFromLink: false, presentationHoldOverlay: false }
)

const { frame, show, assetIndex, bufferIndex, seamlessIncomingFromLink, presentationHoldOverlay } = toRefs(props)
const { state } = usePlayer()
const frameRef = ref<InstanceType<typeof VFrame> | null>(null)
const isRssDirectoryQueueFrame = computed(() => isRssDirectoryFrame(frame.value))

const ctaInterpolationContext = computed(() => {
  const campaign = state.campaign
  const scheduleRootUrn = campaign != null && 'scheduleRootUrn' in campaign
    ? (campaign as IScheduleRootAssignment).scheduleRootUrn
    : null
  return buildCtaInterpolationContext({
    tenantId: state.tenant?.id ?? '',
    playerId: state.playerId,
    playerTitle: state.playerTitle,
    venueId: state.venueId,
    tenantHandle: state.tenantHandle,
    scheduleRootUrn,
    frame: frame.value
  })
})
const containerRef = inject<Ref<HTMLElement | null>>('visibleFrameContainerRef', ref(null))
const preloadNextSlotNow = inject<Ref<boolean>>('preloadNextSlotNow', ref(false))
const suppressHiddenSlotVideoPreload = inject<Ref<boolean>>('suppressHiddenSlotVideoPreload', ref(false))
const playbackQueue = inject<Ref<IQueueFrame[]>>('playbackQueue', ref([]))

function slotMainMediaKind(): string {
  const main = frame.value.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return 'none'
  }
  return assetEntryMediaKind(resolveAssetEntry(facetWithAsset(main)))
}

const effectivePreloadNextSlotNow = computed(() => {
  if (!unref(preloadNextSlotNow)) {
    return false
  }
  if (show.value) {
    return true
  }
  const kind = slotMainMediaKind()
  if (kind === 'image') {
    /** Full-HD bitmap decode on a hidden slot is a common OOM path during catch-up (#579). */
    return false
  }
  if (!unref(suppressHiddenSlotVideoPreload)) {
    return true
  }
  if (kind === 'video-blob' || kind === 'hls') {
    return false
  }
  return true
})

/**
 * Hidden-slot facets must leave the DOM unless this slot is visible or we are preloading the next *video*.
 * Paused/hidden &lt;video&gt; and full-canvas hidden images both composite over the other buffer in Chrome.
 */
const shouldMountNativeFacet = computed(() => {
  if (show.value) {
    return true
  }
  if (!effectivePreloadNextSlotNow.value) {
    return false
  }
  const kind = slotMainMediaKind()
  return kind === 'video-blob' || kind === 'hls'
})
const mainFacetRendersImage = computed(() => {
  const main = frame.value.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return false
  }
  return assetEntryMediaKind(resolveAssetEntry(facetWithAsset(main))) === 'image'
})

/** Progressive MP4 via Media3 underlay — shell must stay transparent so TextureView shows. */
const mainFacetIsNativeVideo = computed(() => {
  const main = frame.value.facets?.main
  if (main == null || typeof main.asset !== 'string' || main.asset.trim() === '') {
    return false
  }
  const entry = resolveAssetEntry(facetWithAsset(main))
  return entry != null
    && typeof entry === 'object'
    && 'isNativeVideo' in entry
    && entry.isNativeVideo === true
})

const continuousMediaOffsetSeconds = computed(() => {
  if (!unref(seamlessIncomingFromLink)) {
    return 0
  }
  return continuousMediaOffsetSecondsForQueueFrame(unref(playbackQueue), unref(frame))
})
const sectionRef0 = inject<Ref<HTMLElement | null>>('widgetSectionRefForIndex0', ref(null))
const sectionRef1 = inject<Ref<HTMLElement | null>>('widgetSectionRefForIndex1', ref(null))
const containerRef0 = inject<Ref<HTMLElement | null>>('frameContainerRefForIndex0', ref(null))
const containerRef1 = inject<Ref<HTMLElement | null>>('frameContainerRefForIndex1', ref(null))

function getSectionRefForIndex(i: number): Ref<HTMLElement | null> | undefined {
  return i === 0 ? sectionRef0 : i === 1 ? sectionRef1 : undefined
}

function getContainerRefForIndex(i: number): Ref<HTMLElement | null> | undefined {
  return i === 0 ? containerRef0 : i === 1 ? containerRef1 : undefined
}

function domRootFromRef(el: unknown): HTMLElement | null {
  if (el == null || typeof el !== 'object') { return null }
  if (el instanceof HTMLElement) { return el }
  const inst = el as { $el?: unknown }
  if (inst.$el instanceof HTMLElement) { return inst.$el }
  return null
}

/** Set the section element ref for this buffer index so WidgetTwoSlotLayer can preposition the slot. */
function setWidgetSectionRef(sectionKey: string, el: unknown, explicitIndex?: number) {
  const idx = explicitIndex ?? bufferIndex?.value ?? 0
  const sectionRef = getSectionRefForIndex(idx)
  if (!sectionRef) { return }
  const key = getActiveWidgetSectionKey(frame.value, assetIndex.value)
  if (sectionKey !== key) { return }
  const element = domRootFromRef(el)
  sectionRef.value = element?.parentElement ?? null
}

function assignContainerRef() {
  const el = frameRef.value?.container ?? null
  if (containerRef) {
    containerRef.value = show.value ? el : containerRef.value
  }
  const idx = bufferIndex?.value ?? 0
  const containerRefForIndex = getContainerRefForIndex(idx)
  if (containerRefForIndex) {
    containerRefForIndex.value = el
  }
}

watch([show, containerRef], () => {
  if (show.value && containerRef) { assignContainerRef() }
}, { immediate: true })

onMounted(assignContainerRef)
onUpdated(assignContainerRef)

onBeforeUnmount(() => {
  const idx = bufferIndex?.value ?? 0
  if (containerRef && frameRef.value?.container && containerRef.value === frameRef.value.container) {
    containerRef.value = null
  }
  const sectionRef = getSectionRefForIndex(idx)
  if (sectionRef) { sectionRef.value = null }
  const containerRefForIndex = getContainerRefForIndex(idx)
  if (containerRefForIndex) { containerRefForIndex.value = null }
})

function resolveAssetEntry(facet: TFacet & { asset: string }): IAssetEntry | undefined {
  return props.assetIndex[facetAssetIndexKey(facet)]
}

function nativeFacetEntryWithVnode(facet: TFacet): (IAssetEntry & { vnode: VNode }) | undefined {
  const f = facetWithAsset(facet)
  if (!f.asset?.trim()) {
    return undefined
  }
  const entry = props.assetIndex[facetAssetIndexKey(f)]
  if (entry && 'vnode' in entry && entry.vnode) {
    return entry as IAssetEntry & { vnode: VNode }
  }
  return undefined
}

function facetWithAsset(facet: TFacet): TFacet & { asset: string } {
  return facet as TFacet & { asset: string }
}

function widgetSuppressionReason(facet: TFacet): string | null {
  const f = facetWithAsset(facet)
  return getWidgetSectionSuppressionReason(frame.value, f, resolveAssetEntry(f))
}

function fallbackSectionReason(facet: TFacet): string {
  return getSectionFallbackSuppressionReason(facet, frame.value, assetIndex.value)
}
</script>
